import React, { useState } from 'react';
import Sidebar from '../Includes/Sidebar';
import GeneralService from '../../services/general.service';
import Footer from '../Includes/Footer';
import { useFormik } from 'formik';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';

export default function AddOpenHours() {
    const [message, setMessage] = useState("");
    const [alertType, setAlertType] = useState(""); // 'success' or 'danger'
    const [isLoading, setIsLoading] = useState(false);
    const state = useSelector((state) => state.stateVals);
    const { id: adminId } = state;

    // ✅ Form Validation Schema for Opening Hours
    const validationSchema = Yup.object({
        day: Yup.string().required("Day is required"),
        open_time: Yup.string().required("Opening time is required"),
        close_time: Yup.string().required("Closing time is required"),
        is_closed: Yup.number().required("Status is required"),
        emergency_only: Yup.number().required("Emergency status is required"),
    });

    const { values, errors, touched, handleBlur, handleChange, handleSubmit, resetForm, setFieldValue } =
        useFormik({
            initialValues: {
                day: '',
                open_time: '',
                close_time: '',
                is_closed: 0,
                emergency_only: 0
            },
            validationSchema,
            onSubmit: (values) => formSubmit(values),
        });

    // ✅ Function to convert day ranges to individual days
    const convertDayRangeToArray = (dayRange) => {
        const dayMap = {
            'Monday': 1, 'Tuesday': 2, 'Wednesday': 3, 'Thursday': 4, 
            'Friday': 5, 'Saturday': 6, 'Sunday': 7
        };
        const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

        switch (dayRange) {
            case 'Monday- Friday':
            case 'Monday - Friday':
                return ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
            case 'Monday - Saturday':
                return ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
            case 'Monday - Sunday':
                return ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
            default:
                return [dayRange]; // Single day
        }
    };

    // ✅ Handle day change and auto-set closed status
    const handleDayChange = (event) => {
        const selectedDay = event.target.value;
        setFieldValue('day', selectedDay);
        
        // Auto-set Sunday as closed if selected
        if (selectedDay === 'Sunday') {
            setFieldValue('is_closed', 1);
            setFieldValue('open_time', '');
            setFieldValue('close_time', '');
        } else {
            setFieldValue('is_closed', 0);
        }
    };

    // ✅ Handle closed status change
    const handleClosedChange = (event) => {
        const isClosed = parseInt(event.target.value);
        setFieldValue('is_closed', isClosed);
        
        if (isClosed === 1) {
            // If closed, clear times
            setFieldValue('open_time', '');
            setFieldValue('close_time', '');
        }
    };
    
// ✅ Form Submission Function - Replace your existing formSubmit function with this
const formSubmit = async (values) => {
    setIsLoading(true);
    setMessage("");

    try {
        // Prepare data according to your API structure
        const hourData = {
            day: values.day,
            open_time: values.is_closed === 1 ? '' : values.open_time,
            close_time: values.is_closed === 1 ? '' : values.close_time,
            is_closed: values.is_closed,
            emergency_only: values.emergency_only,
            admin_id: adminId
        };

        console.log('Submitting hour data:', hourData);

        // ✅ Call the service function
        const response = await GeneralService.addOpeningHour(hourData, adminId);
        
        // ✅ Check response format
        if (response.success) {
            setMessage(response.message || "Opening hour added successfully!");
            setAlertType("success");
            console.log('Created opening hour:', response.data);
            
            // Reset form on success
            resetForm();
        } else {
            setMessage(response.message || "Failed to add opening hour");
            setAlertType("danger");
        }
        
    } catch (err) {
        console.error("Add opening hour error:", err);
        setMessage(err.message || "Error adding opening hour. Please try again.");
        setAlertType("danger");
    }

    setIsLoading(false);
};
    const daysOfWeek = [
        'Monday- Friday', 'Monday - Saturday', 'Monday - Sunday',
    ];

    return (
        <>
            <div className="content">
                <Sidebar />
                <div className="container-fluid pt-4 px-4">
                    <div className="bg-light text-center rounded p-4">
                        <div className="d-flex align-items-center justify-content-between mb-4">
                            <h6 className="mb-0">Add Opening Hours</h6>
                        </div>

                        {/* ✅ Success or Error Message */}
                        {message && (
                            <div className={`alert alert-${alertType} alert-dismissible fade show`} role="alert">
                                {message}
                                <button 
                                    type="button" 
                                    className="btn-close" 
                                    aria-label="Close"
                                    onClick={() => setMessage("")}
                                ></button>
                            </div>
                        )}

                        <form onSubmit={handleSubmit} noValidate>
                            <div className='row'>
                                {/* Day Selection */}
                                <div className='col-md-6'>
                                    <div className="form-floating mb-4">
                                        <select
                                            className={`form-select ${touched.day && errors.day ? 'is-invalid' : ''}`}
                                            id="day"
                                            name="day"
                                            value={values.day}
                                            onChange={handleDayChange}
                                            onBlur={handleBlur}
                                        >
                                            <option value="">Select Day</option>
                                            {daysOfWeek.map(day => (
                                                <option key={day} value={day}>{day}</option>
                                            ))}
                                        </select>
                                        <label htmlFor="day">Day of Week</label>
                                        {touched.day && errors.day && (
                                            <div className="invalid-feedback">{errors.day}</div>
                                        )}
                                    </div>
                                </div>

                                {/* Closed Status */}
                                <div className='col-md-6'>
                                    <div className="form-floating mb-4">
                                        <select
                                            className={`form-select ${touched.is_closed && errors.is_closed ? 'is-invalid' : ''}`}
                                            id="is_closed"
                                            name="is_closed"
                                            value={values.is_closed}
                                            onChange={handleClosedChange}
                                            onBlur={handleBlur}
                                        >
                                            <option value={0}>Open</option>
                                            <option value={1}>Closed</option>
                                        </select>
                                        <label htmlFor="is_closed">Status</label>
                                        {touched.is_closed && errors.is_closed && (
                                            <div className="invalid-feedback">{errors.is_closed}</div>
                                        )}
                                    </div>
                                </div>

                                {/* Opening Time - Only show if not closed */}
                                {values.is_closed === 0 && (
                                    <div className='col-md-6'>
                                        <div className="form-floating mb-4">
                                            <input
                                                type="time"
                                                className={`form-control ${touched.open_time && errors.open_time ? 'is-invalid' : ''}`}
                                                id="open_time"
                                                name="open_time"
                                                value={values.open_time}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                            />
                                            <label htmlFor="open_time">Opening Time</label>
                                            {touched.open_time && errors.open_time && (
                                                <div className="invalid-feedback">{errors.open_time}</div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Closing Time - Only show if not closed */}
                                {values.is_closed === 0 && (
                                    <div className='col-md-6'>
                                        <div className="form-floating mb-4">
                                            <input
                                                type="time"
                                                className={`form-control ${touched.close_time && errors.close_time ? 'is-invalid' : ''}`}
                                                id="close_time"
                                                name="close_time"
                                                value={values.close_time}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                            />
                                            <label htmlFor="close_time">Closing Time</label>
                                            {touched.close_time && errors.close_time && (
                                                <div className="invalid-feedback">{errors.close_time}</div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Emergency Only */}
                                <div className='col-md-6'>
                                    <div className="form-floating mb-4">
                                        <select
                                            className={`form-select ${touched.emergency_only && errors.emergency_only ? 'is-invalid' : ''}`}
                                            id="emergency_only"
                                            name="emergency_only"
                                            value={values.emergency_only}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                        >
                                            <option value={0}>Normal Service</option>
                                            <option value={1}>Emergency Only</option>
                                        </select>
                                        <label htmlFor="emergency_only">Service Type</label>
                                        {touched.emergency_only && errors.emergency_only && (
                                            <div className="invalid-feedback">{errors.emergency_only}</div>
                                        )}
                                    </div>
                                </div>

                                {/* Submit Button */}
                                <div className="col-md-12 d-flex">
                                    <button className="btn btn-submit" type="submit" disabled={isLoading}>
                                        {isLoading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                Adding Hours...
                                            </>
                                        ) : (
                                            "Add Opening Hours"
                                        )}
                                    </button>
                                </div>
                            </div>
                        </form>

                        {/* Display Current Form Values for Debugging */}
                        {/* {process.env.NODE_ENV === 'development' && (
                            <div className="mt-4 p-3 bg-light border rounded">
                                <h6>Debug Info:</h6>
                                <pre>{JSON.stringify(values, null, 2)}</pre>
                            </div>
                        )} */}
                    </div>
                </div>
                <Footer />
            </div>

            {/* ✅ Custom CSS */}
            <style jsx>{`
                .btn-submit {
                    background-color: #f44c00;
                    border-color: #f44c00;
                    padding: 14px 28px;
                    font-size: 16px;
                    font-weight: 600;
                    text-transform: uppercase;
                    border-radius: 30px;
                    transition: background-color 0.3s, border-color 0.3s;
                    display: inline-block;
                    margin-top: 10px;
                    color: #fff;
                }

                .btn-submit:hover {
                    background-color: #d43f00;
                    border-color: #d43f00;
                    color: #fff;
                }

                .btn-submit:focus {
                    outline: none;
                }

                .form-select {
                    padding-top: 1.625rem;
                    padding-bottom: 1.625rem;
                }

                .alert {
                    text-align: left;
                }

                input[type="time"] {
                    padding-top: 1.625rem;
                    padding-bottom: 1.625rem;
                }
            `}</style>
        </>
    );
}